/**
 * Customer Profile Page (T073)
 * Detailed customer view with tabbed sections
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, AlertCircle } from 'lucide-react';
import { useCustomer } from '../hooks/useCustomers';
import CustomerOverview from '../components/Customers/CustomerOverview';
import AppointmentHistory from '../components/Customers/AppointmentHistory';
import InvoiceHistory from '../components/Customers/InvoiceHistory';
import PaymentHistory from '../components/Customers/PaymentHistory';
import CommunicationLog from '../components/Customers/CommunicationLog';

const tabs = ['Overview', 'Appointments', 'Invoices', 'Payments', 'Communications'] as const;
type Tab = (typeof tabs)[number];

export default function CustomerProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const customerId = id ?? '';
  const { data: customer, isLoading, error } = useCustomer(customerId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading customer profile...</p>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="space-y-6 pb-8">
        <button
          onClick={() => navigate('/customers')}
          className="claude-button claude-button-ghost text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </button>
        <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Failed to load customer</p>
              <p className="text-sm mt-1 opacity-90">
                The customer could not be found or there was a server error.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return <CustomerOverview customer={customer} />;
      case 'Appointments':
        return <AppointmentHistory customerId={customer.id} />;
      case 'Invoices':
        return <InvoiceHistory customerId={customer.id} />;
      case 'Payments':
        return <PaymentHistory customerId={customer.id} customerName={customer.name} />;
      case 'Communications':
        return <CommunicationLog customerId={customer.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/customers')}
        className="claude-button claude-button-ghost text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Customers
      </button>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User className="w-7 h-7 text-primary" />
        </div>
        <div>
          <h1>{customer.name}</h1>
          <p className="text-muted-foreground">{customer.email}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex gap-1 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
}
